import React, { useState, useEffect, useRef } from 'react';
import './forgetpassword.scss';
import { useNavigate } from 'react-router-dom';
import { Image_Url } from "../../ImageUrl/ImageUrl";
import { forgotPasswordAction, verifyForgotPasswordOtp } from '../../actions/passwordAction';
import { notify } from '../../services/notification';
import { useDispatch } from 'react-redux';
import { EMAIL_REGEX } from '../../constants/regex/regex';

const ForgetPasswordForm = () => {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // OTP modal states
  const [isOtpStep, setIsOtpStep] = useState(false);
  const [otp, setOtp] = useState('');
  const [otpTimer, setOtpTimer] = useState(0);
  const [canResendOtp, setCanResendOtp] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);

  const otpRefs = useRef([]);
  const intervalRef = useRef(null);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  useEffect(() => {
    if (isOtpStep && otpTimer > 0 && !intervalRef.current) {
      intervalRef.current = setInterval(() => {
        setOtpTimer((prev) => {
          if (prev <= 1) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
            setCanResendOtp(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [isOtpStep, otpTimer]);

  const isValidEmail = EMAIL_REGEX.test(email);

  const handleInputChange = (e) => {
    const value = e.target.value;
    setEmail(value);
    if (error) setError('');
  };

  const handleSendOtp = async (e) => {
    e.preventDefault();
    if (!email) {
      setError('Please enter your email address');
      return false;
    }
    if (!isValidEmail) {
      setError('Please enter a valid email address');
      return false;
    }
    if (isSubmitting) return;
    setError('');
    setIsSubmitting(true);
    try {
      let response = await dispatch(forgotPasswordAction({ email,is_reset_login: true }));
      if (response?.data?.success === true) {
        notify.success(response.data.message);
        setIsOtpStep(true);
        const expirySeconds = response?.data?.meta?.resend_otp_seconds;
        if (expirySeconds !== undefined) {
          setOtpTimer(expirySeconds);
          setCanResendOtp(false);
        }
      }
    } catch (err) {
      if (err?.response?.status === 429) {
        notify.error(err.response.data?.message || 'Too many requests. Please try again later.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOtpChange = (e, index) => {
    const value = e.target.value;
    if (!/^\d?$/.test(value)) return;

    let newOtp = otp.split('');
    newOtp[index] = value;
    newOtp = newOtp.join('');
    setOtp(newOtp);

    if (value && index < 5) {
      otpRefs.current[index + 1].focus();
    }
  };

  const handleOtpKeyDown = (e, index) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      otpRefs.current[index - 1].focus();
    }
  };

  const handleVerifyOtp = async () => {
    const otpRegex = /^\d{6}$/;
    if (!otp) {
      notify.error('Please enter OTP');
      return;
    }
    if (!otpRegex.test(otp)) {
      notify.error('OTP must be exactly 6 digits');
      return;
    }

    setIsVerifying(true);
    try {
      const response = await dispatch(verifyForgotPasswordOtp({
        email,
        otp,
        is_reset_login: true
      }));
      

      if (response?.data?.success === true) {
        const token = response?.data?.data?.user?.token;
        if (token) {
          sessionStorage.setItem('resetToken', token);
          notify.success(response?.data.message);
          navigate('/resetpassword');
        } else {
          notify.error('Something went wrong. Please try again.');
        }
      }
    } catch (err) {
      if (err?.response?.status === 429) {
        notify.error(err.response.data?.message || 'Too many requests. Please try again later.');
      }
    } finally {
      setIsVerifying(false);
    }
  };

  const handleResendOtp = async () => {
    if (!canResendOtp) return;
    setIsSubmitting(true);
    try {
      const response = await dispatch(forgotPasswordAction({ email, is_reset_login: true }));
      if (response?.data?.success === true) {
        notify.success(response.data.message);
        setOtp('');
        setCanResendOtp(false);
        const expirySeconds = response?.data?.meta?.resend_otp_seconds;
        if (expirySeconds !== undefined) {
          setOtpTimer(expirySeconds);
        }
      }
    } catch (err) {
      if (err?.response?.status === 429) {
        notify.error(err.response.data?.message || 'Too many requests. Please try again later.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseOtpModal = () => {
    setIsOtpStep(false);
    setOtp('');
    setOtpTimer(0);
    setCanResendOtp(false);
  };

  return (
    <div className='forget-container'>
      <div className='tvs-logo'>
        <img src={Image_Url.TVSLOGO} alt='' />
      </div>
      <div className='reset-text'>Reset your password</div>
      <div className="forget-password-container">
        <div className="password-instruction-text">
          <span>Enter your user account's</span>
          <span>verified email address and we will send you an OTP.</span>
        </div>
        <form className='form-submit' onSubmit={handleSendOtp}>
          <input type="email" className='input-text-color' value={email} placeholder="Enter your email" onChange={handleInputChange} />
          {error && <div className="error-message">{error}</div>}
          <button className='submit_btn' type="submit" disabled={!isValidEmail || isSubmitting}>
            {isSubmitting ? 'Sending...' : 'Send OTP'}
          </button>
          <div className='back_btn'>
            <span onClick={() => navigate("/")} >Back To Login</span>
          </div>
        </form>
      </div>

      {isOtpStep && (
        <div className='otp-modal-overlay'>
          <div className='otp-modal-content'>
            {isVerifying ? (
              <div className='otp-skeleton-wrapper'>
                <div className='skeleton-shimmer skeleton-title'></div>
                <div className='skeleton-shimmer skeleton-subtitle'></div>
                <div className='skeleton-otp-boxes'>
                  {[...Array(6)].map((_, i) => (
                    <div key={i} className='skeleton-shimmer skeleton-otp-box'></div>
                  ))}
                </div>
                <div className='skeleton-shimmer skeleton-btn'></div>
                <div className='skeleton-shimmer skeleton-resend'></div>
              </div>
            ) : (
              <>
                <div className='otp-modal-header'>
                  <h3>Verify OTP</h3>
                  <span className='otp-modal-close' onClick={handleCloseOtpModal}>&times;</span>
                </div>
                <p className='otp-modal-subtitle'>Enter the 6-digit OTP sent to your email</p>
                <div className="otpContainer">
                  {[...Array(6)].map((_, index) => (
                    <input
                      key={index}
                      type="text"
                      maxLength={1}
                      inputMode="numeric"
                      className="otpBox"
                      value={otp[index] || ''}
                      onChange={(e) => handleOtpChange(e, index)}
                      onKeyDown={(e) => handleOtpKeyDown(e, index)}
                      ref={(el) => (otpRefs.current[index] = el)}
                    />
                  ))}
                </div>
                <button className='otp-verify-btn' type="button" onClick={handleVerifyOtp} disabled={isVerifying}>
                  Verify
                </button>
                <div className='otp-resend-section'>
                  {canResendOtp ? (
                    <span className="resendOtp" onClick={handleResendOtp}>Resend OTP</span>
                  ) : (
                    <span className="resendOtpDisabled">Resend OTP in {otpTimer}s</span>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ForgetPasswordForm;
